import {
  keepPreviousData, useMutation, useQuery, useQueryClient
} from '@tanstack/react-query';

import APIGateway from '@/api';
import {
  Source,
  SourceCreateReq,
  SourceGetRes,
  SourcePreview,
  SourcesSearchParams,
  SourcesSearchRes,
  SourceUpdateReq
} from '@/types/evals';


export const sourcesKeys = {
  all: [ 'sources' ] as const,
  search: ( params?: SourcesSearchParams ) => [ ...sourcesKeys.all, 'search', params ] as const,
  detail: ( id: string, version?: number | 'latest' ) => [ ...sourcesKeys.all, 'detail', id, version ] as const,
  preview: ( id: string, version?: number | 'latest', count?: number ) => [ ...sourcesKeys.all, 'preview', id, version, count ] as const
};

export function useSourcesSearch( params: SourcesSearchParams ) {
  return useQuery<{ items: Source[]; hasMore: boolean; nextCursor: string | null }>( {
    refetchOnWindowFocus: false,
    queryKey: sourcesKeys.search( params ),
    queryFn: async() => {
      const res: SourcesSearchRes = await APIGateway.evals.sourcesGet( params );
      return { items: res.items, hasMore: res.pagination.has_more, nextCursor: res.pagination.next_cursor };
    },
    placeholderData: keepPreviousData
  } );
}

export function useSource( sourceId: string, version: number | 'latest' = 'latest', enabled: boolean = true ) {
  return useQuery<SourceGetRes>( {
    refetchOnWindowFocus: false,
    enabled: enabled && !!sourceId,
    queryKey: sourcesKeys.detail( sourceId, version ),
    queryFn: async() => {
      return await APIGateway.evals.sourceGet( sourceId, version );
    }
  } );
}

export function useSourcePreview(
  sourceId: string,
  version: number | 'latest' = 'latest',
  count: number = 5,
  enabled: boolean = true
) {
  return useQuery<SourcePreview>( {
    refetchOnWindowFocus: false,
    enabled: enabled && !!sourceId,
    queryKey: sourcesKeys.preview( sourceId, version, count ),
    queryFn: async() => {
      return await APIGateway.evals.sourcePreviewGet( sourceId, version, count );
    }
  } );
}

export function useCreateSource() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: SourceCreateReq ) => {
      return await APIGateway.evals.sourceCreate( args );
    },
    onSuccess: () => {
      // Invalidate and refetch sources after successful creation
      queryClient.invalidateQueries( { queryKey: sourcesKeys.all } );
    }
  } );
}

export function useUpdateSourceVersion() {
  const queryClient = useQueryClient();

  return useMutation( {
    mutationFn: async( args: { sourceId: string; data: SourceUpdateReq } ) => {
      return await APIGateway.evals.sourceVersionCreate( args.sourceId, args.data );
    },
    onSuccess: ( _data, variables ) => {
      // Invalidate source detail and preview queries
      queryClient.invalidateQueries( { queryKey: sourcesKeys.detail( variables.sourceId ) } );
      queryClient.invalidateQueries( { queryKey: sourcesKeys.preview( variables.sourceId ) } );
      queryClient.invalidateQueries( { queryKey: sourcesKeys.all } );
    }
  } );
}
